import {
  Account,
  AccountMilestoneWithRequirements,
  AccountRequirementStatus,
  MilestoneTemplateType,
  MilestoneType,
  Packages,
  QueueDocumentResponse,
  QueueStatus,
  RequirementsWithRequirements,
} from 'types';

/**
 * The milestone lifecycle is a state machine owned by the backend: `setCurrentMilestone` advances
 * `account.currentMilestone` through per-program ordered milestone templates. This module is the
 * frontend's pure projection of that state — it never re-derives transitions, it only maps the
 * current/completed milestone into what the completion banner should show. Adding a program type
 * requires no change here; adding a milestone type is a compile error (see MILESTONE_TYPE_TO_PACKAGE).
 */

export enum MilestoneBannerCopyVariant {
  ntpGranted = 'ntpGranted',
  loopFieldComplete = 'loopFieldComplete',
  installApproved = 'installApproved',
  generic = 'generic',
}

export interface MilestoneCompletionProps {
  completedPackage?: { type: Packages; name?: string };
  currentPackage: { type: Packages; name?: string };
  account: Account;
  completedAt?: Date;
  latestCompletedMilestone?: AccountMilestoneWithRequirements;
  ptoQueue?: QueueDocumentResponse;
  hasDesignDocsQueue?: boolean;
  hasPtoFlagsEnabled: boolean;
  copyVariant: MilestoneBannerCopyVariant;
}

type MilestoneBannerState = { visible: false } | { visible: true; props: MilestoneCompletionProps };

interface MilestoneBannerFlags {
  flagDrivenPackageReview: boolean;
  usePtoQueue: boolean;
  ptoGreenlight: boolean;
}

interface MilestoneBannerInput {
  account?: Account;
  latestCompletedMilestone?: AccountMilestoneWithRequirements;
  ptoQueue?: QueueDocumentResponse;
  hasDesignDocsQueue: boolean;
  noRejectionOnPackage: boolean;
  flags: MilestoneBannerFlags;
}

/**
 * Which package a milestone asks the user to submit. Total over MilestoneTemplateType (the type of
 * `currentMilestone.type`), so a new milestone type fails to compile here rather than silently
 * yielding no banner. Milestones without a submittable package map to null.
 */
const MILESTONE_TYPE_TO_PACKAGE: Record<MilestoneTemplateType, Packages | null> = {
  [MilestoneType.loopField]: Packages.loopFieldPackage,
  [MilestoneType.install]: Packages.installPackage,
  [MilestoneType.activation]: Packages.activationPackage,
  [MilestoneType.postActivation]: Packages.postActivationPackage,
  [MilestoneType.noticeToProceed]: null,
  [MilestoneType.customerAgreement]: null,
  [MilestoneType.customerVerification]: null,
  [MilestoneType.initialAccountDetails]: null,
};

/** Banner copy keyed on the milestone that was just completed. Anything unlisted uses `generic`. */
const COMPLETION_COPY_VARIANT: Partial<Record<MilestoneType, MilestoneBannerCopyVariant>> = {
  [MilestoneType.noticeToProceed]: MilestoneBannerCopyVariant.ntpGranted,
  [MilestoneType.loopField]: MilestoneBannerCopyVariant.loopFieldComplete,
  [MilestoneType.install]: MilestoneBannerCopyVariant.installApproved,
};

const PACKAGE_LABEL: Record<Packages, string> = {
  [Packages.installPackage]: 'Installation Package',
  [Packages.activationPackage]: 'Activation Package',
  [Packages.postActivationPackage]: 'Post Activation Package',
  [Packages.loopFieldPackage]: 'Loop Field Package',
};

const packageTypeForMilestoneType = (type: MilestoneTemplateType | undefined): Packages | null => {
  if (!type) return null;
  return MILESTONE_TYPE_TO_PACKAGE[type];
};

interface DetectedPackage {
  type: Packages;
  name?: string;
  requirement?: RequirementsWithRequirements;
}

const detectPackage = (
  milestoneType: MilestoneTemplateType | undefined,
  requirements: RequirementsWithRequirements[] | undefined,
): DetectedPackage | undefined => {
  const packageType = packageTypeForMilestoneType(milestoneType);
  if (!packageType) return undefined;
  const requirement = requirements?.find((req) => req.type === (packageType as string));
  return { type: packageType, name: requirement?.name ?? PACKAGE_LABEL[packageType], requirement };
};

/**
 * Pure projection of an account's milestone state into the completion-banner decision. Returns
 * `{ visible: false }` unless the current milestone has a pending package, the review flag is on,
 * there are no package rejections, and program-specific gates pass.
 */
export const resolveMilestoneCompletionBanner = ({
  account,
  latestCompletedMilestone,
  ptoQueue,
  hasDesignDocsQueue,
  noRejectionOnPackage,
  flags,
}: MilestoneBannerInput): MilestoneBannerState => {
  const currentMilestone = account?.currentMilestone;
  const hasPtoFlagsEnabled = flags.usePtoQueue && flags.ptoGreenlight;

  const currentPackage = detectPackage(currentMilestone?.type, currentMilestone?.requirements);

  const showPackageCompletionBanner =
    noRejectionOnPackage &&
    currentPackage?.requirement?.status === AccountRequirementStatus.pending &&
    flags.flagDrivenPackageReview;

  if (!account || !currentPackage || !showPackageCompletionBanner) {
    return { visible: false };
  }

  // Per FIN-2990, the Activation banner is hidden until the project has PTO Greenlight.
  if (
    hasPtoFlagsEnabled &&
    currentPackage.type === Packages.activationPackage &&
    !account.ptoGreenlight?.greenlightStatus &&
    ptoQueue?.status !== QueueStatus.approved
  ) {
    return { visible: false };
  }

  const completedPackage = detectPackage(latestCompletedMilestone?.type, latestCompletedMilestone?.requirements);
  const copyVariant =
    (latestCompletedMilestone?.type && COMPLETION_COPY_VARIANT[latestCompletedMilestone.type]) ??
    MilestoneBannerCopyVariant.generic;

  return {
    visible: true,
    props: {
      completedPackage: completedPackage ? { type: completedPackage.type, name: completedPackage.name } : undefined,
      currentPackage: { type: currentPackage.type, name: currentPackage.name },
      account,
      completedAt: completedPackage?.requirement?.completedAt,
      latestCompletedMilestone,
      ptoQueue,
      hasDesignDocsQueue,
      hasPtoFlagsEnabled,
      copyVariant,
    },
  };
};
